import React, { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, CardHeader, Grid, TextField, Button,
  Checkbox, FormControlLabel, Stack, Divider
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import CancelIcon from '@mui/icons-material/Cancel'
import SearchIcon from '@mui/icons-material/Search'
import CategoryIcon from '@mui/icons-material/Category'
import { useSnackbar } from 'notistack'
import normaService from '../services/normaService'
import crudsService from '../services/crudsService'
import CrudTable from '../components/common/CrudTable'
import PageHeader from '../components/common/PageHeader'
import ConfirmDialog from '../components/common/ConfirmDialog'

const COLUMNS = [
  { field: 'descripcion', header: 'Descripción' },
  { field: 'numero_requerido_label', header: 'Nro. Requerido' },
]

export default function TipoNormaLegal() {
  const { enqueueSnackbar } = useSnackbar()

  const [casoUso, setCasoUso] = useState('new')
  const [id, setId] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [numeroRequerido, setNumeroRequerido] = useState(true)
  const [buscar, setBuscar] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [rowToDelete, setRowToDelete] = useState(null)

  useEffect(() => { loadData() }, [])

  const loadData = (filtro = '') => {
    setLoading(true)
    normaService.getTiposNormas(null, filtro || undefined).then(res => {
      const list = (res.data?.data || []).map(r => ({
        ...r,
        numero_requerido_label: r.numero_requerido ? 'Sí' : 'No',
      }))
      setRows(list)
    }).catch(() => enqueueSnackbar('Error al cargar tipos de norma', { variant: 'error' }))
      .finally(() => setLoading(false))
  }

  const clearForm = () => {
    setCasoUso('new'); setId(''); setDescripcion(''); setNumeroRequerido(true)
  }

  const handleEdit = (row) => {
    setCasoUso('edit')
    setId(row.id)
    setDescripcion(row.descripcion)
    setNumeroRequerido(row.numero_requerido !== false && row.numero_requerido !== 'No')
  }

  const handleDeleteRequest = (row) => { setRowToDelete(row); setConfirmOpen(true) }

  const handleDeleteConfirm = async () => {
    setConfirmOpen(false)
    try {
      await crudsService.tipoNormaLegal('delete', { id: rowToDelete.id })
      enqueueSnackbar('Tipo de Norma Legal eliminado', { variant: 'success' })
      loadData(buscar)
    } catch { enqueueSnackbar('Error al eliminar', { variant: 'error' }) }
    setRowToDelete(null)
  }

  const handleAceptar = async () => {
    if (!descripcion.trim()) {
      enqueueSnackbar('Debe indicar la descripción del Tipo de Norma Legal', { variant: 'warning' }); return
    }
    const object = { descripcion, numero_requerido: numeroRequerido }
    if (casoUso === 'edit') object.id = id
    try {
      const res = await crudsService.tipoNormaLegal(casoUso, object)
      if (res.data?.code !== undefined) {
        enqueueSnackbar(`Tipo de Norma Legal ${casoUso === 'new' ? 'agregado' : 'actualizado'}`, { variant: 'success' })
        clearForm(); loadData(buscar)
      } else if (res.data?.message_code === 'err-tipo-nl-already-exist') {
        enqueueSnackbar('Ya se ha registrado este Tipo de Norma Legal', { variant: 'warning' })
      } else {
        enqueueSnackbar('Error al guardar', { variant: 'error' })
      }
    } catch { enqueueSnackbar('Error al guardar', { variant: 'error' }) }
  }

  return (
    <Box>
      <PageHeader title="Tipo de Norma Legal" subtitle="Gestión de tipos de normas legales" icon={CategoryIcon} />

      <Grid container spacing={3}>
        {/* Formulario */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader
              title={casoUso === 'new' ? 'Nuevo Tipo' : 'Editar Tipo'}
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
            />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  fullWidth size="small"
                  label="Descripción *"
                  value={descripcion}
                  onChange={e => setDescripcion(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                />
                <FormControlLabel
                  control={<Checkbox checked={numeroRequerido} onChange={e => setNumeroRequerido(e.target.checked)} />}
                  label="Número requerido"
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button variant="outlined" startIcon={<CancelIcon />} onClick={clearForm} size="small">Cancelar</Button>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleAceptar} size="small">
                    {casoUso === 'new' ? 'Agregar' : 'Actualizar'}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Listado"
              titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" placeholder="Buscar..." value={buscar} onChange={e => setBuscar(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadData(buscar)} sx={{ width: 160 }} />
                  <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => loadData(buscar)}>Buscar</Button>
                </Stack>
              }
            />
            <CardContent sx={{ p: 0 }}>
              <CrudTable
                columns={COLUMNS}
                rows={rows}
                loading={loading}
                onEdit={handleEdit}
                onDelete={handleDeleteRequest}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={confirmOpen}
        title="Eliminar Tipo de Norma Legal"
        message={`¿Confirma la eliminación de "${rowToDelete?.descripcion}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setRowToDelete(null) }}
      />
    </Box>
  )
}
